<section class="recon-page">
  <div class="page-head">
    <div>
      <h1>Reconciliation result</h1>
      <p class="intro">Review the generated reconciliation summary, issues, and items.</p>
    </div>

    <div class="head-actions">
      <a class="btn ghost" [routerLink]="['/app/statements', statementId]">Back to statement</a>
      <button type="button" class="btn secondary" (click)="loadRun()">Refresh</button>
    </div>
  </div>

  @if (isLoading) {
    <div class="card">
      <p>Loading reconciliation result...</p>
    </div>
  }

  @if (!isLoading && errorMessage) {
    <div class="message error-banner">{{ errorMessage }}</div>
  }

  @if (!isLoading && run) {
    <div class="card">
      <div class="detail-grid">
        <div class="detail-row">
          <span class="label">Statement ID</span>
          <span>{{ run.statementId }}</span>
        </div>
        <div class="detail-row">
          <span class="label">Run ID</span>
          <span>{{ run.runId }}</span>
        </div>
        <div class="detail-row">
          <span class="label">Engine version</span>
          <span>{{ run.engineVersion }}</span>
        </div>
        <div class="detail-row">
          <span class="label">Generated at</span>
          <span>{{ run.generatedAt | date:'medium' }}</span>
        </div>
      </div>
    </div>

    @if (run.summary) {
      <div class="summary-grid">
        <div class="card stat-card">
          <span class="stat-label">Items</span>
          <strong>{{ run.summary.itemCount }}</strong>
        </div>
        <div class="card stat-card">
          <span class="stat-label">Issues</span>
          <strong>{{ run.summary.issueCount }}</strong>
        </div>
        <div class="card stat-card">
          <span class="stat-label">Unknown</span>
          <strong>{{ run.summary.unknownCount }}</strong>
        </div>
        <div class="card stat-card">
          <span class="stat-label">Total in</span>
          <strong>{{ formatMoney(run.summary.totalIn) }}</strong>
        </div>
        <div class="card stat-card">
          <span class="stat-label">Total out</span>
          <strong>{{ formatMoney(run.summary.totalOut) }}</strong>
        </div>
        <div class="card stat-card">
          <span class="stat-label">Fees</span>
          <strong>{{ formatMoney(run.summary.totalFees) }}</strong>
        </div>
      </div>
    }

    <div class="card section-card">
      <div class="section-head">
        <h2>Issues</h2>
        <span class="badge">{{ run.issues.length }}</span>
      </div>

      @if (run.issues.length === 0) {
        <p class="empty-text">No issues were reported for this run.</p>
      } @else {
        <div class="issue-list">
          @for (issue of run.issues; track issue.message + $index) {
            <div class="issue-item">
              <div class="issue-head">
                <strong>{{ issue.message }}</strong>
                <span class="issue-meta">
                  {{ issue.severity || 'UNKNOWN' }} / {{ issue.code || 'N/A' }}
                </span>
              </div>

              @if (issue.statementLineId) {
                <p class="subtle">Statement line: {{ issue.statementLineId }}</p>
              }

              @if (hasDetails(issue.details)) {
                <pre class="details-json">{{ issue.details | json }}</pre>
              }
            </div>
          }
        </div>
      }
    </div>

    <div class="card section-card">
      <div class="section-head">
        <h2>Items</h2>
        <span class="badge">{{ run.items.length }}</span>
      </div>

      @if (run.items.length === 0) {
        <p class="empty-text">No reconciliation items were generated.</p>
      } @else {
        <div class="table-wrap">
          <table class="items-table">
            <thead>
            <tr>
              <th>Date</th>
              <th>Title</th>
              <th>Description</th>
              <th>Category</th>
              <th>Direction</th>
              <th>Amount</th>
              <th>Confidence</th>
            </tr>
            </thead>
            <tbody>
              @for (item of run.items; track item.itemId) {
                <tr>
                  <td>{{ item.bookingDate || '—' }}</td>
                  <td>{{ item.title || '—' }}</td>
                  <td>{{ item.description || '—' }}</td>
                  <td>{{ item.category || '—' }}</td>
                  <td>{{ item.netDirection || '—' }}</td>
                  <td>{{ formatMoney(item.netAmount) }}</td>
                  <td>{{ item.confidence ?? '—' }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>
  }
</section>
